import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export type WhatsNewBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'image'; url: string };

export interface WhatsNewTranslation {
  title: string;
  blocks: WhatsNewBlock[];
}

// Per-locale overrides; missing locales fall back to the English base
// (title/blocks on the entry itself). Codes match the mobile app's i18n.
export type WhatsNewLocale = 'ua' | 'ru' | 'sk' | 'de';
export type WhatsNewTranslations = Partial<
  Record<WhatsNewLocale, WhatsNewTranslation>
>;

export interface WhatsNewEntry {
  id: string;
  title: string;
  blocks: WhatsNewBlock[];
  translations: WhatsNewTranslations | null;
  iosVersion: string;
  androidVersion: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertWhatsNewPayload {
  title?: string;
  blocks?: WhatsNewBlock[];
  translations?: WhatsNewTranslations | null;
  iosVersion?: string;
  androidVersion?: string;
  isPublished?: boolean;
}

export const whatsNewApi = createApi({
  reducerPath: 'whatsNewApi',
  baseQuery,
  tagTypes: ['WhatsNewList'],
  endpoints: (builder) => ({
    listWhatsNew: builder.query<WhatsNewEntry[], void>({
      query: () => ({ url: '/admin/whats-new' }),
      providesTags: ['WhatsNewList'],
    }),
    createWhatsNew: builder.mutation<WhatsNewEntry, UpsertWhatsNewPayload>({
      query: (body) => ({
        url: '/admin/whats-new',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WhatsNewList'],
    }),
    updateWhatsNew: builder.mutation<
      WhatsNewEntry,
      { id: string } & UpsertWhatsNewPayload
    >({
      query: ({ id, ...body }) => ({
        url: `/admin/whats-new/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['WhatsNewList'],
    }),
    deleteWhatsNew: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/admin/whats-new/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WhatsNewList'],
    }),
    uploadWhatsNewImage: builder.mutation<{ url: string }, FormData>({
      query: (body) => ({
        url: '/admin/whats-new/upload-image',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useListWhatsNewQuery,
  useCreateWhatsNewMutation,
  useUpdateWhatsNewMutation,
  useDeleteWhatsNewMutation,
  useUploadWhatsNewImageMutation,
} = whatsNewApi;
